import { handleApiResponse, isAbortError } from '@src/api/apiClient/axiosClient';
import { channelApiClient } from '@src/api/shared/community/channelApiClient';
import {
  Chat,
  chatApiClient,
  ChatGroup,
  ChatIndexRequest,
  ChatIndexResponse,
  PinnedChat,
} from '@src/api/shared/community/chatApiClient';
import { Pagination } from '@src/api/types/pagination';
import { useIsModal } from '@src/context/IsModalContext';
import { useReactionContext } from '@src/context/ReactionContext';
import { CHAT_CHANGE_TYPE } from '@src/enum/chatChangeTypeEnum';
import {
  CHAT_CHANGES_POLLING_INTERVAL,
  POST_SEND_POLL_DELAY,
} from '@src/features/shared/community/constants/polling';
import { clearPreservedUnreadLine } from '@src/store/channelSlice';
import { RootState } from '@src/store/store.ts';
import logger from '@src/utils/logger';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';

import { useDebouncedEffect } from './useDebouncedEffect';

export type UseChatMessagesResult = {
  chats: ChatGroup[];
  pinnedChat?: PinnedChat;
  setPinnedChat: Dispatch<SetStateAction<PinnedChat | undefined>>;
  loadMoreChats: () => void;
  loadMoreNewerChats: () => Promise<void>;
  hasMoreChats: boolean;
  hasMoreNewerChats: boolean;
  isValidating: boolean;
  isDataReady: boolean;
  hideUnreadLines: () => void;
  mutate: () => void;
  firstUnreadChatId: number | null;
  schedulePostSendPoll: () => void;
};

const emptyPagination: Pagination = {
  total: 0,
  per_page: 30,
  current_page: 1,
  last_page: 1,
  path: '',
  from: 0,
  to: 0,
  first_page_url: '',
  last_page_url: '',
  next_page_url: null,
  prev_page_url: null,
};

const emptyChatIndexResponse: ChatIndexResponse = {
  data: [],
  pagination: emptyPagination,
  is_access_denied: false,
  first_unread_chat_id: null,
};

/**
 * 複数のチャットグループをマージし、重複するチャットは最新の情報で統合する
 * リアクションの更新やメッセージの編集内容を反映するために使用
 */
const mergeChatGroups = (groups: ChatGroup[]): ChatGroup[] => {
  // すべてのチャットを平坦化
  const allChats: { date_label: string; chat: Chat }[] = [];
  for (const group of groups) {
    for (const chat of group.chats) {
      allChats.push({ date_label: group.date_label, chat });
    }
  }

  // チャットIDをキーとしたMapで重複を処理
  const chatMap = new Map<number, { date_label: string; chat: Chat }>();
  for (const item of allChats) {
    const existingItem = chatMap.get(item.chat.id);
    if (existingItem) {
      // 同じIDのチャットが既に存在する場合、最新の情報でマージ
      // 新しいメッセージ内容、編集フラグ、リアクション、添付ファイルなどを反映
      chatMap.set(item.chat.id, {
        ...existingItem,
        chat: {
          ...existingItem.chat,
          // メッセージ内容：新しい値があれば更新、なければ既存を保持
          message: item.chat.message || existingItem.chat.message,
          // 編集フラグ：undefinedでない場合は新しい値を使用
          is_edited:
            item.chat.is_edited !== undefined ? item.chat.is_edited : existingItem.chat.is_edited,
          // 更新日時：新しい値があれば更新
          updated_at: item.chat.updated_at || existingItem.chat.updated_at,
          // リアクション：新しいリアクション情報があれば更新
          reactions: item.chat.reactions || existingItem.chat.reactions,
          // 添付ファイル：新しい添付ファイル情報があれば更新
          attachments: item.chat.attachments || existingItem.chat.attachments,
        },
      });
    } else {
      // 新しいチャットの場合はそのまま追加
      chatMap.set(item.chat.id, item);
    }
  }

  const grouped: Record<string, ChatGroup> = {};
  for (const { date_label, chat } of chatMap.values()) {
    if (!grouped[date_label]) {
      grouped[date_label] = { date_label, chats: [] };
    }
    grouped[date_label].chats.push(chat);
  }

  return Object.values(grouped)
    .map((group) => ({
      ...group,
      chats: group.chats.sort((a, b) => a.id - b.id),
    }))
    .sort((a, b) => {
      const dateA = convertDateLabelToDate(a.date_label);
      const dateB = convertDateLabelToDate(b.date_label);
      return dateA.getTime() - dateB.getTime();
    });
};

const convertDateLabelToDate = (dateLabel: string): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (dateLabel === '今日') {
    return today;
  }
  if (dateLabel === '昨日') {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    return yesterday;
  }
  const currentYear = new Date().getFullYear();
  const yearHandledDateLabel = dateLabel.replace(/(\d+)月(\d+)日 \(.\)/, `${currentYear}-$1-$2`);
  const parsedDate = new Date(yearHandledDateLabel);
  if (isNaN(parsedDate.getTime())) {
    return new Date(0);
  }
  return parsedDate;
};

export const useChatMessages = (channelCode?: string, chatId?: number): UseChatMessagesResult => {
  const disabled = !channelCode;
  const dispatch = useDispatch();
  const preservedUnreadLines = useSelector(
    (state: RootState) => state.channel.preservedUnreadLines,
  );

  // AbortController management - チャンネルごとに適切に管理
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const pollingAbortControllersRef = useRef<Map<string, AbortController>>(new Map());

  const latestIdRef = useRef<number | null>(null);
  const newestIdRef = useRef<number | null>(null);
  const firstChatIdRef = useRef<number | null>(null);
  const [latestChats, setLatestChats] = useState<ChatGroup[]>([]);
  const [newerChats, setNewerChats] = useState<ChatGroup[]>([]);
  const [pinnedChat, setPinnedChat] = useState<PinnedChat | undefined>();
  const isFileDownloading = useSelector((state: RootState) => state.fileDownload.isFileDownloading);
  const { isAnyModalOpen } = useIsModal();
  const { forceUpdateReactions } = useReactionContext();

  const [initPage, setInitPage] = useState<number | null>(null);
  const [firstUnreadChatId, setFirstUnreadChatId] = useState<number | null>(null);
  const [isAtLatestPage, setIsAtLatestPage] = useState<boolean>(false);
  const [currentPageState, setCurrentPageState] = useState<number | null>(null);
  const [hasMoreNewerChats, setHasMoreNewerChats] = useState<boolean>(false);
  const [lastChangesTimestamp, setLastChangesTimestamp] = useState<string | null>(null);
  const [deletedChatIds, setDeletedChatIds] = useState<Set<number>>(new Set());
  const [chatChangesMap, setChatChangesMap] = useState<Map<number, Partial<Chat>>>(new Map());

  const isUpdatingRef = useRef(false);
  const pollChangesRef = useRef<(() => Promise<void>) | null>(null);

  const getAbortController = (channelCode: string): AbortController => {
    if (!abortControllersRef.current.has(channelCode)) {
      abortControllersRef.current.set(channelCode, new AbortController());
    }
    return abortControllersRef.current.get(channelCode)!;
  };

  const getPollingAbortController = (channelCode: string): AbortController => {
    if (!pollingAbortControllersRef.current.has(channelCode)) {
      pollingAbortControllersRef.current.set(channelCode, new AbortController());
    }
    return pollingAbortControllersRef.current.get(channelCode)!;
  };

  const fetchPinnedChat = async (code: string, signal?: AbortSignal) => {
    const res = await channelApiClient.show(code, undefined, signal);
    let pinned: PinnedChat | undefined;
    handleApiResponse(res, (data) => {
      pinned = data?.channel?.pinned_chat || undefined;
    });
    return pinned;
  };

  const updatePinnedChat = useCallback(async (code: string, signal?: AbortSignal) => {
    try {
      const pinned = await fetchPinnedChat(code, signal);
      setPinnedChat(pinned);
    } catch (error) {
      if (!isAbortError(error)) {
        console.error('Failed to update pinned chat:', error);
      }
    }
  }, []);

  const isReady = channelCode && initPage !== null;

  const getKey = (
    pageIndex: number,
    previousPageData: ChatIndexResponse | null,
  ): [string, string, number] | null => {
    const index = initPage ? initPage + pageIndex : pageIndex + 1;

    if (disabled) {
      return null;
    }
    if (previousPageData && pageIndex + 1 > previousPageData.pagination.last_page) {
      return null;
    }
    return ['chatMessage', channelCode, index];
  };

  const fetcher = async ([, code, page]: [string, string, number]): Promise<ChatIndexResponse> => {
    if (disabled) {
      return emptyChatIndexResponse;
    }

    const isFirstPage = page === (initPage || 1);
    const params: ChatIndexRequest = {
      page,
      after_id: latestIdRef.current ?? undefined,
      ...(isFirstPage && { mark_as_read: true }),
    };

    const res = await chatApiClient.index(code, params, getAbortController(code).signal);

    let result = emptyChatIndexResponse;
    handleApiResponse(res, (data) => {
      result = data;
      setFirstUnreadChatId(data.first_unread_chat_id);
    });

    return result;
  };

  const { data, size, setSize, isValidating, mutate } = useSWRInfinite<ChatIndexResponse, [string]>(
    isReady ? getKey : () => null,
    fetcher,
    {
      revalidateFirstPage: true,
      revalidateAll: true,
      revalidateOnMount: true,
      dedupingInterval: 0,
      keepPreviousData: false,
      compare: (a, b) => JSON.stringify(a) === JSON.stringify(b),
      revalidateOnFocus: false,
    },
  );

  const pagedChats = useMemo(() => {
    const pages = data ?? [];

    const sortedPages = [...pages].sort(
      (a, b) => b.pagination.current_page - a.pagination.current_page,
    );

    const all = sortedPages.flatMap((page) => page.data);
    const allChats = all.flatMap((group) => group.chats);

    if (allChats.length > 0) {
      const page1Data = pages.find((page) => page.pagination.current_page === 1);
      if (page1Data) {
        const page1Chats = page1Data.data.flatMap((group) => group.chats);
        if (page1Chats.length > 0) {
          latestIdRef.current = page1Chats[page1Chats.length - 1].id ?? null;
        }
      } else {
        latestIdRef.current = allChats[allChats.length - 1].id ?? null;
      }
    }

    return all;
  }, [data]);

  useEffect(() => {
    if (!data || data.length === 0) {
      return;
    }

    const hasPageOne = data.some((page) => page?.pagination?.current_page === 1);

    const hasNewestData = latestIdRef.current !== null;

    setIsAtLatestPage(hasPageOne || (hasNewestData && currentPageState === 1));
  }, [data, currentPageState]);

  const isPagedChatReady = data !== undefined && data.length > 0;

  const { data: latestData } = useSWR<ChatIndexResponse>(
    isPagedChatReady &&
      channelCode &&
      latestIdRef.current &&
      isAtLatestPage &&
      !isFileDownloading &&
      !isAnyModalOpen &&
      !isUpdatingRef.current
      ? ['latestChats', channelCode, latestIdRef.current]
      : null,
    async ([, code, updateAfter]: [string, string, number?]) => {
      if (isUpdatingRef.current) {
        return emptyChatIndexResponse;
      }

      isUpdatingRef.current = true;

      try {
        const params: ChatIndexRequest = {
          page: 'latest',
          after_id: updateAfter ?? undefined,
          is_polling: true,
        };

        const res = await chatApiClient.index(code, params, getPollingAbortController(code).signal);

        let result = emptyChatIndexResponse;
        handleApiResponse(res, (data) => {
          result = data;
          if (data.first_unread_chat_id) {
            setFirstUnreadChatId(data.first_unread_chat_id);
          }
        });

        void updatePinnedChat(code, getPollingAbortController(code).signal);

        return result;
      } catch (error) {
        if (!isAbortError(error)) {
          console.error('Failed to fetch latest chats:', error);
        }
        return emptyChatIndexResponse;
      } finally {
        isUpdatingRef.current = false;
      }
    },
    {
      refreshInterval: 5000,
      revalidateOnFocus: false,
      revalidateIfStale: false,
      dedupingInterval: 3000,
    },
  );

  useEffect(() => {
    if (!chatId || !channelCode) {
      setInitPage(1);
      return;
    }

    chatApiClient.page(channelCode, chatId).then((res) => {
      if (res.result !== 'success') {
        setInitPage(1);
        return;
      }
      setInitPage(res.data.page);
    });
  }, [channelCode, chatId]);

  useEffect(() => {
    if (channelCode && firstUnreadChatId && !isPagedChatReady) {
      mutate();
    }
  }, [channelCode, firstUnreadChatId, isPagedChatReady, mutate]);

  const prevChannelCodeRef = useRef<string | undefined>(undefined);

  // チャンネル変更時のAbortController管理
  useEffect(() => {
    const prevChannelCode = prevChannelCodeRef.current;

    // 初回レンダリング時は単に記録するだけ
    if (prevChannelCode === undefined && channelCode) {
      prevChannelCodeRef.current = channelCode;
      return;
    }

    if (prevChannelCode === channelCode) {
      return;
    }

    // 前のチャンネルのリクエストをキャンセル（存在する場合のみ）
    if (prevChannelCode) {
      const prevAbortController = abortControllersRef.current.get(prevChannelCode);
      const prevPollingAbortController = pollingAbortControllersRef.current.get(prevChannelCode);

      if (prevAbortController) {
        prevAbortController.abort();
        abortControllersRef.current.delete(prevChannelCode);
      }
      if (prevPollingAbortController) {
        prevPollingAbortController.abort();
        pollingAbortControllersRef.current.delete(prevChannelCode);
      }
    }

    // 新しいチャンネル用のAbortControllerは必要時に自動作成される
    prevChannelCodeRef.current = channelCode;
  }, [channelCode]);

  const prevLatestDataRef = useRef<ChatIndexResponse | undefined>();
  const latestChatsRef = useRef<ChatGroup[]>([]);

  useEffect(() => {
    latestChatsRef.current = latestChats;
  }, [latestChats]);

  useEffect(() => {
    if (latestData && latestData !== prevLatestDataRef.current) {
      prevLatestDataRef.current = latestData;

      if (latestData.data && latestData.data.length > 0) {
        const combined = [...latestChatsRef.current, ...latestData.data];
        setLatestChats(mergeChatGroups(combined));
      }

      if (
        latestData.first_unread_chat_id !== undefined &&
        latestData.first_unread_chat_id !== firstUnreadChatId
      ) {
        setFirstUnreadChatId(latestData.first_unread_chat_id);
      }
    }
  }, [latestData, firstUnreadChatId]);

  const prevChannelRef = useRef<string | undefined>();

  useEffect(() => {
    const prevChannel = prevChannelRef.current;

    if (prevChannel && prevChannel !== channelCode && channelCode) {
      dispatch(clearPreservedUnreadLine(prevChannel));

      (async () => {
        try {
          const params: ChatIndexRequest = {
            page: 1,
            mark_as_read: true,
          };
          await chatApiClient.index(prevChannel, params);
        } catch (error) {
          logger.error('Failed to mark previous channel as read:', error);
        }
      })();
    }

    if (channelCode) {
      prevChannelRef.current = channelCode;
    }
  }, [channelCode, dispatch]);

  useEffect(() => {
    setLatestChats([]);
    setNewerChats([]);
    latestIdRef.current = null;
    newestIdRef.current = null;
    firstChatIdRef.current = null;
    setPinnedChat(undefined);
    setFirstUnreadChatId(null);

    void updatePinnedChat(channelCode ?? '');
    mutate();
  }, [channelCode, mutate, updatePinnedChat]);

  const mergedChats = useMemo(() => {
    if (!isPagedChatReady) {
      return [];
    }
    const combined = mergeChatGroups([...pagedChats, ...latestChats, ...newerChats]);
    return combined
      .map((group) => ({
        ...group,
        chats: group.chats
          .filter((chat) => !deletedChatIds.has(chat.id))
          .map((chat) =>
            chatChangesMap.has(chat.id) ? { ...chat, ...chatChangesMap.get(chat.id)! } : chat,
          ),
      }))
      .filter((group) => group.chats.length > 0);
  }, [pagedChats, latestChats, newerChats, isPagedChatReady, deletedChatIds, chatChangesMap]);

  const [debouncedLatestId, setDebouncedLatestId] = useState<number | null>(null);
  useDebouncedEffect(
    () => {
      const allChats = mergedChats.flatMap((group) => group.chats);
      if (allChats.length > 0) {
        const newLatestId = allChats[allChats.length - 1].id ?? null;
        if (newLatestId !== debouncedLatestId) {
          setDebouncedLatestId(newLatestId);
          latestIdRef.current = newLatestId;
        }
      }
    },
    [mergedChats],
    1000,
  );

  const lastPage = data?.[data.length - 1];
  const hasMoreChats =
    lastPage?.pagination !== undefined
      ? lastPage.pagination.current_page < lastPage.pagination.last_page
      : false;

  const isDataReady = useMemo(() => {
    return data !== undefined && data.length > 0 && mergedChats.length > 0;
  }, [data, mergedChats]);

  const displayFirstUnreadChatId = useMemo(() => {
    if (channelCode && preservedUnreadLines[channelCode]) {
      return preservedUnreadLines[channelCode];
    }
    return firstUnreadChatId;
  }, [channelCode, preservedUnreadLines, firstUnreadChatId]);

  const hideUnreadLines = useCallback(async () => {
    if (channelCode) {
      try {
        const params: ChatIndexRequest = {
          page: 1,
          mark_as_read: true,
        };

        await chatApiClient.index(channelCode, params, getAbortController(channelCode).signal);

        dispatch(clearPreservedUnreadLine(channelCode));
        setFirstUnreadChatId(null);
        mutate();
      } catch (error) {
        logger.error('Failed to mark as read:', error);
        setFirstUnreadChatId(null);
        mutate();
      }
    }
  }, [channelCode, dispatch, mutate]);

  useEffect(() => {
    return () => {
      isUpdatingRef.current = false;
    };
  }, []);

  const schedulePostSendPoll = useCallback(() => {
    setTimeout(() => void pollChangesRef.current?.(), POST_SEND_POLL_DELAY);
  }, []);

  useEffect(() => {
    if (initPage) {
      setCurrentPageState(initPage);
      setHasMoreNewerChats(initPage > 1);
    }
  }, [initPage]);

  // Function for loading newer chats when scrolling down
  const loadMoreNewerChats = useCallback(async () => {
    if (!channelCode || !currentPageState || currentPageState <= 1) {
      return;
    }

    const nextPage = currentPageState - 1;

    const res = await chatApiClient.index(
      channelCode,
      {
        page: nextPage,
      },
      getAbortController(channelCode).signal,
    );

    handleApiResponse(res, (data) => {
      if (data.data.length > 0) {
        mutate((currentData) => {
          if (!currentData) {
            return currentData;
          }

          const newPageData = {
            ...data,
            data: data.data,
          };

          return [newPageData, ...currentData];
        }, false);

        setCurrentPageState(nextPage);

        setHasMoreNewerChats(nextPage > 1);
      } else {
        setHasMoreNewerChats(false);
      }
    });

    setCurrentPageState(nextPage);
  }, [channelCode, currentPageState, mutate]);

  useEffect(() => {
    if (!channelCode || !isDataReady) {
      pollChangesRef.current = null;
      return;
    }

    const pollChanges = async () => {
      if (isFileDownloading || isAnyModalOpen) {
        return;
      }

      const since = lastChangesTimestamp || new Date().toISOString();
      const res = await chatApiClient.changes(
        channelCode,
        { since },
        getPollingAbortController(channelCode).signal,
      );

      handleApiResponse(res, (data) => {
        const { data: changesData, latest_timestamp } = data;

        if (Array.isArray(changesData)) {
          const newChanges = new Map(chatChangesMap);
          const deletedIds = new Set(deletedChatIds);

          changesData.forEach((change) => {
            const chatId = Number(change.id);

            if (change.type === CHAT_CHANGE_TYPE.DELETED) {
              deletedIds.add(chatId);
              newChanges.delete(chatId);
              return;
            }

            const isEdited = change.type === CHAT_CHANGE_TYPE.EDITED;
            newChanges.set(chatId, {
              ...newChanges.get(chatId),
              ...(isEdited && {
                message: change.message,
                is_edited: true,
                updated_at: change.updated_at,
                attachments: change.attachments,
              }),
              ...(change.reactions !== undefined && { reactions: change.reactions }),
              ...(change.threads !== undefined && { threads: change.threads }),
              ...(change.preview !== undefined && { preview: change.preview }),
            });

            if (change.reactions) {
              forceUpdateReactions(chatId, change.reactions);
            }
          });

          setChatChangesMap(newChanges);
          setDeletedChatIds(deletedIds);
        }
        setLastChangesTimestamp(latest_timestamp);
      });
    };

    pollChangesRef.current = pollChanges;

    const interval: NodeJS.Timeout = setInterval(pollChanges, CHAT_CHANGES_POLLING_INTERVAL);

    return () => {
      clearInterval(interval);
      pollChangesRef.current = null;
    };
  }, [
    channelCode,
    isDataReady,
    lastChangesTimestamp,
    deletedChatIds,
    chatChangesMap,
    forceUpdateReactions,
    isFileDownloading,
    isAnyModalOpen,
  ]);

  return {
    chats: mergedChats,
    pinnedChat,
    setPinnedChat,
    loadMoreChats: () => setSize(size + 1),
    loadMoreNewerChats, // for infinite scroll down
    hasMoreChats,
    hasMoreNewerChats, // for infinite scroll down
    isValidating,
    firstUnreadChatId: displayFirstUnreadChatId,
    isDataReady,
    hideUnreadLines,
    schedulePostSendPoll,
    mutate: async () => {
      if (channelCode && !isFileDownloading && !isAnyModalOpen) {
        await updatePinnedChat(channelCode, getAbortController(channelCode).signal);
      }
      if (!isFileDownloading && !isAnyModalOpen) {
        await mutate();
      }
    },
  };
};
